/**
 * API 서비스 기본 클래스
 * 
 * 모든 API 호출의 기반이 되는 클래스입니다.
 * - 인증 토큰 자동 추가
 * - 401 에러 자동 처리
 * - 에러 핸들링 통합
 * 
 * 사용법:
 * import api from '../services/api';
 * const data = await api.get('/reservations');
 */

import { buildAuthHeaders, ensureCsrfToken, handleAuthError } from '../utils/auth';

const extractFilenameFromDisposition = (contentDisposition) => {
    if (!contentDisposition) {
        return null;
    }

    const utf8Match = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
        try {
            return decodeURIComponent(utf8Match[1]);
        } catch (error) {
            return utf8Match[1];
        }
    }

    const plainMatch = contentDisposition.match(/filename\s*=\s*"?([^";]+)"?/i);
    return plainMatch?.[1] || null;
};

const isStateChangingMethod = (method = 'GET') => ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase());
const CSRF_ERROR_PATTERN = /CSRF token missing|Invalid CSRF token/i;

const parseErrorResponse = async (response) => {
    const errorData = await response.json().catch(() => ({}));
    const message = errorData.message || errorData.error || `HTTP ${response.status}`;
    const error = new Error(message);
    error.status = response.status;
    error.data = errorData;
    return error;
};

class ApiService {
    constructor(baseUrl = '/api') {
        this.baseUrl = baseUrl;
    }

    buildUrl(endpoint) {
        return `${this.baseUrl}${endpoint}`;
    }

    createRequestConfig(options = {}, { includeJsonContentType = true } = {}) {
        return {
            ...options,
            headers: {
                ...buildAuthHeaders({
                    ...(includeJsonContentType ? { 'Content-Type': 'application/json' } : {}),
                    ...options.headers,
                }),
            },
            credentials: 'include',
        };
    }

    async requestResponse(endpoint, options = {}, configOptions = {}) {
        const config = this.createRequestConfig(options, configOptions);
        const method = (config.method || 'GET').toUpperCase();

        if (isStateChangingMethod(method)) {
            const csrfToken = await ensureCsrfToken();

            if (csrfToken) {
                config.headers = {
                    ...config.headers,
                    'X-CSRF-Token': csrfToken,
                };
            }
        }

        try {
            const response = await fetch(this.buildUrl(endpoint), config);

            if (response.status === 401) {
                handleAuthError(response);
                throw new Error('Unauthorized');
            }

            if (!response.ok) {
                const error = await parseErrorResponse(response);
                const csrfMessage = error.data?.error || error.data?.message || error.message;

                if (
                    isStateChangingMethod(method) &&
                    response.status === 403 &&
                    !config._csrfRetried &&
                    CSRF_ERROR_PATTERN.test(csrfMessage)
                ) {
                    const refreshedCsrfToken = await ensureCsrfToken({ forceRefresh: true });

                    if (refreshedCsrfToken) {
                        return this.requestResponse(
                            endpoint,
                            {
                                ...options,
                                headers: {
                                    ...options.headers,
                                    'X-CSRF-Token': refreshedCsrfToken,
                                },
                                _csrfRetried: true,
                            },
                            configOptions,
                        );
                    }
                }

                throw error;
            }

            return response;
        } catch (error) {
            if (error.name === 'TypeError' && error.message.includes('fetch')) {
                console.error('Network error:', error);
                throw new Error('네트워크 연결을 확인해주세요.');
            }

            console.error(`API Error [${endpoint}]:`, error);
            throw error;
        }
    }

    /**
     * 기본 요청 메서드
     * @param {string} endpoint - API 엔드포인트 (예: '/reservations')
     * @param {Object} options - fetch 옵션
     * @returns {Promise<any>} 응답 데이터
     */
    async request(endpoint, options = {}) {
        const response = await this.requestResponse(endpoint, options);

        if (response.status === 204) {
            return null;
        }

        return response.json();
    }

    /**
     * GET 요청
     * @param {string} endpoint - API 엔드포인트
     * @param {Object} params - 쿼리 파라미터
     */
    get(endpoint, params = {}) {
        const query = new URLSearchParams(params).toString();
        const url = query ? `${endpoint}?${query}` : endpoint;
        return this.request(url, { method: 'GET' });
    }

    /**
     * POST 요청
     * @param {string} endpoint - API 엔드포인트
     * @param {Object} data - 요청 바디
     */
    post(endpoint, data) {
        return this.request(endpoint, {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }

    /**
     * PUT 요청
     * @param {string} endpoint - API 엔드포인트
     * @param {Object} data - 요청 바디
     */
    put(endpoint, data) {
        return this.request(endpoint, {
            method: 'PUT',
            body: JSON.stringify(data)
        });
    }

    /**
     * PATCH 요청
     * @param {string} endpoint - API 엔드포인트
     * @param {Object} data - 요청 바디
     */
    patch(endpoint, data) {
        return this.request(endpoint, {
            method: 'PATCH',
            body: JSON.stringify(data)
        });
    }

    /**
     * DELETE 요청
     * @param {string} endpoint - API 엔드포인트
     */
    delete(endpoint) {
        return this.request(endpoint, { method: 'DELETE' });
    }

    /**
     * FormData POST 요청 (파일 업로드용)
     * @param {string} endpoint - API 엔드포인트
     * @param {FormData} formData - FormData 객체
     */
    upload(endpoint, formData) {
        return this.requestResponse(endpoint, {
            method: 'POST',
            body: formData,
        }, {
            includeJsonContentType: false,
        }).then((response) => response.json());
    }

    async download(endpoint, fallbackFilename = 'download') {
        const response = await this.requestResponse(endpoint, {
            method: 'GET',
        }, {
            includeJsonContentType: false,
        });

        const blob = await response.blob();
        const contentDisposition = response.headers.get('Content-Disposition');
        const filename = extractFilenameFromDisposition(contentDisposition) || fallbackFilename;

        const objectUrl = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(objectUrl);

        return { blob, filename };
    }
}

// 싱글톤 인스턴스
export const api = new ApiService();

// ========================================
// 예약 이미지 API 함수들
// ========================================

/**
 * 예약에 이미지 업로드 (복수 가능)
 * @param {number} reservationId - 예약 ID
 * @param {File[]} files - 이미지 파일 배열
 * @returns {Promise<{success: boolean, images: Array}>}
 */
export async function uploadReservationImages(reservationId, files) {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    return api.upload(`/reservations/${reservationId}/images`, formData);
}

/**
 * 예약 이미지 삭제
 * @param {number} reservationId - 예약 ID
 * @param {number} imageId - 이미지 ID
 * @returns {Promise<{success: boolean}>}
 */
export async function deleteReservationImage(reservationId, imageId) {
    return api.delete(`/reservations/${reservationId}/images/${imageId}`);
}

/**
 * 예약 이미지 순서 변경
 * @param {number} reservationId - 예약 ID
 * @param {number[]} order - 이미지 ID 순서 배열
 * @returns {Promise<{success: boolean}>}
 */
export async function reorderReservationImages(reservationId, order) {
    return api.put(`/reservations/${reservationId}/images/reorder`, { order });
}

/**
 * 예약 이미지 목록 조회
 * @param {number} reservationId - 예약 ID
 * @returns {Promise<{images: Array}>}
 */
export async function getReservationImages(reservationId) {
    return api.get(`/reservations/${reservationId}/images`);
}

// ========================================
// 수동예약 이미지 API 함수들
// ========================================

/**
 * 수동예약에 이미지 업로드 (복수 가능)
 * @param {number} reservationId - 수동예약 ID
 * @param {File[]} files - 이미지 파일 배열
 * @returns {Promise<{success: boolean, images: Array}>}
 */
export async function uploadManualReservationImages(reservationId, files) {
    const formData = new FormData();
    files.forEach(file => formData.append('images', file));
    return api.upload(`/manual-reservations/${reservationId}/images`, formData);
}

/**
 * 수동예약 이미지 삭제
 * @param {number} reservationId - 수동예약 ID
 * @param {number} imageId - 이미지 ID
 * @returns {Promise<{success: boolean}>}
 */
export async function deleteManualReservationImage(reservationId, imageId) {
    return api.delete(`/manual-reservations/${reservationId}/images/${imageId}`);
}

/**
 * 수동예약 이미지 목록 조회
 * @param {number} reservationId - 수동예약 ID
 * @returns {Promise<{images: Array}>}
 */
export async function getManualReservationImages(reservationId) {
    return api.get(`/manual-reservations/${reservationId}/images`);
}

export default api;
